import React, { useState } from "react";
import {
  Box,
  Tooltip,
  IconButton,
  Typography,
  Chip,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import { Link } from "react-router-dom";
import IUser from "../../entities/IUser";
import UserService from "../../services/UserService";
import { getErrorMessage, unwrapData } from "../../services/apiUtils";
import { getUserId } from "../../services/userUtils";
import { theme } from "../../theme";
import EntityDetailDialog from "../common/EntityDetailDialog";
import ConfirmDialog from "../common/ConfirmDialog";

type UserActionsProps = {
  user: IUser;
  onDeleted?: () => void;
};

const getEditLink = (userId: number) => `/create-users/${userId}`;

const getPrimaryAddress = (user: IUser) => {
  const list = user.addressList || [];
  return list.find((a) => a.isDefaultAddress) || list[0] || null;
};

const getDisplayName = (user: IUser) =>
  user.name || [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username || "User";

const UserActions: React.FC<UserActionsProps> = ({ user, onDeleted }) => {
  const userId = getUserId(user);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewUser, setViewUser] = useState<IUser>(user);
  const [viewLoading, setViewLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const address = getPrimaryAddress(viewUser);
  const displayName = getDisplayName(viewUser);

  const handleView = () => {
    setViewOpen(true);
    setViewUser(user);
    setViewLoading(true);
    UserService.getUserById(userId)
      .then((res: any) => {
        const data = unwrapData(res) as IUser;
        if (data) setViewUser(data);
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(err, "Could not load the latest details for this user."),
          severity: "error",
        });
      })
      .finally(() => setViewLoading(false));
  };

  const handleDelete = () => {
    setDeleting(true);
    UserService.deleteUser(userId)
      .then(() => {
        setSnackbar({ open: true, message: "User deleted.", severity: "success" });
        setConfirmOpen(false);
        onDeleted?.();
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(err, "Could not delete this user."),
          severity: "error",
        });
      })
      .finally(() => setDeleting(false));
  };

  return (
    <Box>
      <Tooltip title="View user details">
        <IconButton onClick={handleView} sx={{ color: theme.palette.secondary.main }}>
          <PreviewIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit user details">
        <Link to={getEditLink(userId)}>
          <IconButton sx={{ color: theme.palette.primary.main }}>
            <EditIcon />
          </IconButton>
        </Link>
      </Tooltip>
      <Tooltip title="Delete user">
        <IconButton onClick={() => setConfirmOpen(true)} sx={{ color: theme.status.danger }}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>

      <EntityDetailDialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        icon={<PersonRoundedIcon />}
        title={displayName}
        subtitle={`User ID: ${userId}`}
        editTo={getEditLink(userId)}
        editLabel="Edit User"
      >
        {viewLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <EmailRoundedIcon fontSize="small" color="action" />
                <Typography variant="body2">{viewUser.email || viewUser.emailId || "-"}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneRoundedIcon fontSize="small" color="action" />
                <Typography variant="body2">{viewUser.phone || viewUser.mobile || "-"}</Typography>
              </Stack>
              {address && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <PlaceRoundedIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                  <Typography variant="body2">
                    {address.fullAddress ||
                      [address.address1, address.address2, address.cityName].filter(Boolean).join(", ")}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Box display="flex" gap={1} flexWrap="wrap">
              {viewUser.userRole?.roleName && (
                <Chip
                  size="small"
                  label={viewUser.userRole.roleName}
                  color={viewUser.userRole.roleName === "ADMIN" ? "secondary" : "default"}
                  variant="outlined"
                />
              )}
              <Chip
                size="small"
                label={viewUser.enabled ? "Active" : "Inactive"}
                color={viewUser.enabled ? "success" : "default"}
              />
              {viewUser.gender && <Chip size="small" label={viewUser.gender} variant="outlined" />}
            </Box>
          </>
        )}
      </EntityDetailDialog>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete User"
        message={`Are you sure you want to delete "${displayName}"? This cannot be undone.`}
        loading={deleting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserActions;
